/** Mitteilungen: Erlaubnis, Abo beim Server, Zustand für die Diagnose. */
import { api, path } from './api';

export type PushSupport = 'ok' | 'not-installed' | 'unsupported';

/** Läuft die App vom Home-Bildschirm? (Nur dort erlaubt iOS Mitteilungen.) */
export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
}

export function isIos(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function pushSupport(): PushSupport {
  if (!('serviceWorker' in navigator)) return 'unsupported';
  if (!('PushManager' in window) || typeof Notification === 'undefined') {
    return isIos() && !isStandalone() ? 'not-installed' : 'unsupported';
  }
  return 'ok';
}

export function permission(): NotificationPermission | 'unsupported' {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

function b64urlToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Erlaubnis holen und beim Server anmelden. MUSS direkt aus einem Fingertipp
 * aufgerufen werden (iOS verlangt eine direkte Nutzer-Aktion), daher kein
 * await vor requestPermission.
 */
export async function enablePush(): Promise<{ ok: boolean; message: string }> {
  if (pushSupport() === 'not-installed') return { ok: false, message: 'Erst zum Home-Bildschirm hinzufügen und von dort öffnen.' };
  if (pushSupport() !== 'ok') return { ok: false, message: 'Dieses Gerät unterstützt keine Mitteilungen.' };
  const result = await Notification.requestPermission();
  if (result !== 'granted') return { ok: false, message: 'Mitteilungen wurden nicht erlaubt. Ändern: Einstellungen → Mitteilungen → Pflanzen.' };
  await syncSubscription();
  return { ok: true, message: 'Mitteilungen sind aktiv.' };
}

/** Abo anlegen oder erneuern und beim Server melden (bei jedem Start – repariert sich so selbst). */
export async function syncSubscription(): Promise<boolean> {
  if (pushSupport() !== 'ok' || permission() !== 'granted') return false;
  const reg = await navigator.serviceWorker.ready;
  const { publicKey } = await api<{ publicKey: string }>('GET', path('pushKey'));
  let sub = await reg.pushManager.getSubscription();
  const key = b64urlToBytes(publicKey);
  const current = sub?.options.applicationServerKey;
  const same = current && new Uint8Array(current).every((b, i) => b === key[i]);
  if (sub && !same) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api('POST', path('pushSubscribe'), { endpoint: json.endpoint, keys: json.keys, userAgent: navigator.userAgent.slice(0, 380) });
  return true;
}

export async function sendTestPush(): Promise<{ ok: boolean; sent: number; error: string | null }> {
  return api('POST', path('pushTest'));
}

/** Zahl am App-Symbol (nur in der installierten App). */
export async function setBadge(count: number): Promise<void> {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try {
    if (count > 0) await nav.setAppBadge?.(count);
    else await nav.clearAppBadge?.();
  } catch {
    /* nicht unterstützt */
  }
}
